// ----------------- Imports -----------------
import React, { useMemo } from "react"
// Components
import { MainLayout } from "components/Layout"
import SectionV2 from "components/Sections/SectionV2"
import BrandingLayoutSkeleton from "components/Skeleton/BrandingLayoutSkeleton"
import CustomImage from "components/atoms/Image/CustomImage"

// Hooks
import { useLang } from "hooks/useLang"

// Constants
import SocialMediaCard from "components/EventDetailsPage/SocialMediaCard"
import dynamic from "next/dynamic"

// ----------------- Interface -----------------
interface BrandingLayoutProps {
    isLoading: boolean
    data: any // You can replace `any` with a more specific type based on your data structure
    name?: string
    description?: string
    poster: string
    logo: string
    subTitle?: string
    payload?: any
    location?: {
        country: string
        address: string
        city: string
        coords: {
            lat: number
            lng: number
        }
    }
}

const LiveMap = dynamic(() => import("../../components/LiveMap/liveMap"), {
    ssr: false,
})
// ----------------- Component -----------------
const BrandingLayout: React.FC<BrandingLayoutProps> = ({
    isLoading,
    data,
    name,
    description,
    poster,
    logo,
    subTitle,
    payload,
    location,
}) => {
    const { t, lang } = useLang()
    const isArabic = lang === "ar"
    // Styles
    const blurStyle = useMemo(
        () => ({
            filter: `blur(100px)`,
            opacity: 1,
            transform: "scale(1)",
            willChange: "filter",
        }),
        []
    )

    if (isLoading) {
        return (
            <MainLayout>
                <BrandingLayoutSkeleton />
            </MainLayout>
        )
    }

    return (
        <>
            {/* ----------------- Page Content ----------------- */}
            <MainLayout>
                <div>
                    {/* Blur image background */}
                    <div
                        style={blurStyle}
                        className="fixed left-0 top-0 -z-10 md:aspect-video h-full md:max-h-screen w-full overflow-hidden md:block"
                    >
                        <div className="h-full lg-60 sm:h-full max-h-screen w-full overflow-hidden md:block">
                            <CustomImage
                                src={
                                    process.env.NEXT_PUBLIC_Event_Img_URL +
                                    poster
                                }
                                alt={name!}
                                fill
                                className="object-cover transition-opacity duration-1000 opacity-100 z-10 absolute top-0 left-0 w-full h-full"
                            />
                        </div>
                    </div>

                    {/* Hero section */}
                    <div className="layout lg:max-w-[1230px] mt-[120px] md:mt-[0px]">
                        <div className="w-full lg:mt-22 mt-20 relative">
                            <div className="relative lg:aspect-[16/8] aspect-[11/16] rounded-[15px] w-full shadow-md overflow-hidden">
                                {/* Background Image */}
                                <CustomImage
                                    src={
                                        process.env.NEXT_PUBLIC_Event_Img_URL +
                                        poster
                                    }
                                    alt="Single-Ad"
                                    fill
                                    className="object-cover rounded-[15px]"
                                />

                                {/* Blur Overlay */}
                                <div className="absolute inset-0 bg-black/40 backdrop-blur-xl rounded-[15px] z-10" />

                                {/* Content */}
                                <div className="absolute inset-0 flex justify-center md:justify-start z-20">
                                    <div className="flex flex-col w-full md:w-1/2 p-5 md:p-[60px] items-center md:items-start">
                                        {/* Subtitle */}
                                        <div className="py-2">
                                            <span className="capitalize">
                                                {t(subTitle!)}
                                            </span>
                                        </div>

                                        {/* Logo */}
                                        <div className="w-[125px] h-[125px] backdrop-blur-sm rounded-[15px] relative">
                                            <CustomImage
                                                src={
                                                    process.env
                                                        .NEXT_PUBLIC_Event_Img_URL +
                                                    logo
                                                }
                                                alt="Single-Ad"
                                                fill
                                                className="rounded-[15px] object-contain"
                                            />
                                        </div>

                                        {/* Event Name & Description */}
                                        <div className="flex flex-col items-center md:items-start flex-1 w-full gap-6 mt-4">
                                            <span
                                                className={`text-[26px] md:text-50px text-white font-bold text-center ${isArabic ? "md:text-right" : "md:text-left"} capitalize flex`}
                                            >
                                                {name}
                                            </span>

                                            <p className="text-white text-[16px]">
                                                {description}
                                            </p>

                                            {/* Social Media */}
                                            <div className="flex flex-row gap-6 md:gap-4 flex-wrap justify-start mt-2">
                                                <SocialMediaCard event={data} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Venue Section */}
                                    {subTitle === "venue" && (
                                        <div className="hidden md:flex flex-col w-1/2 p-5 md:p-[60px] items-center md:items-start">
                                            <div className="py-2">
                                                <span className="font-bold text-[20px]">
                                                    {t("location")}
                                                </span>
                                            </div>

                                            <div className="w-full flex flex-col gap-4">
                                                {/* City & Address */}
                                                <div className="w-full rounded-[10px] bg-[#D9D9D933] p-10 flex flex-col gap-4">
                                                    <div className="flex flex-row gap-20">
                                                        <span className="font-bold text-white">
                                                            {t("city")}
                                                        </span>
                                                        <span className="text-white">
                                                            {t(location?.city!)}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-row gap-20">
                                                        <span className="font-bold text-white">
                                                            {t("address")}
                                                        </span>
                                                        <span className="text-white text-start">
                                                            {t(
                                                                location?.address!
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Map */}
                                                <div className="flex flex-col w-full relative h-[250px] md:h-[400px] lg:h-[500px] rounded-[10px] overflow-hidden">
                                                    <LiveMap
                                                        title=""
                                                        longitude={
                                                            location?.coords
                                                                ?.lng!
                                                        }
                                                        latitude={
                                                            location?.coords
                                                                ?.lat!
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Events Section */}
                    {payload?.current.length > 0 && (
                        <SectionV2
                            id="section"
                            events={payload?.current}
                            title={t("current_events")}
                            maxEventsInARow={4}
                            layout="grid"
                            aspectRatio="widescreen"
                        />
                    )}
                    {payload?.past?.length > 0 && (
                        <SectionV2
                            id="section"
                            events={payload?.past}
                            title={t("past_events")}
                            maxEventsInARow={4}
                            layout="grid"
                            aspectRatio="widescreen"
                        />
                    )}
                </div>
            </MainLayout>
        </>
    )
}

export default BrandingLayout
